/**
 * TileRenderer — creates, destroys, and styles Phaser tile objects.
 * Supports sparse mask-based grids via Map<cellKey, {tile, text}>.
 */

declare const Phaser: any;

import { cellKey } from "../../../../framework/geometry";
import { Depth } from "../../../../framework/Depth";
import type { GridLayout } from "./GridConfig";
import type { GridMask } from "./GridMask";

export type TileState = "active" | "disabled" | "frozen";

export const TILE_COLORS = {
  active:   { fill: 0x374151, stroke: 0x4b5563 },
  selected: { fill: 0x1d4ed8, stroke: 0x3b82f6 },
  adjacent: { fill: 0x15803d, stroke: 0x22c55e },
  disabled: { fill: 0x1f2937, stroke: 0x374151 },
  frozen:   { fill: 0x164e63, stroke: 0x0891b2 },
  invalid:  { fill: 0x991b1b, stroke: 0x991b1b },
};

export interface TileEntry {
  tile: any;
  text: any;
}

export interface SparseObjects {
  map: Map<string, TileEntry>;
}

/** Create tile objects for each cell in the mask. */
export function createMaskTiles(
  scene: any,
  mask: GridMask,
  layout: GridLayout,
  tileSize: number,
  tileGap: number,
  letters: Map<string, string>,
  onTileClick: (row: number, col: number) => void,
): SparseObjects {
  const map = new Map<string, TileEntry>();
  const stride = tileSize + tileGap;
  const fontSize = `${Math.max(14, Math.floor(tileSize * 0.45))}px`;

  for (const cell of mask.toArray()) {
    const key = cellKey(cell);
    const cx = cell.col - layout.baseCol;
    const ry = cell.row - layout.baseRow;
    const tx = layout.originX + cx * stride + tileSize / 2;
    const ty = layout.originY + ry * stride + tileSize / 2;

    const tile = scene.add.rectangle(tx, ty, tileSize, tileSize, TILE_COLORS.active.fill);
    tile.setStrokeStyle(2, TILE_COLORS.active.stroke);
    tile.setDepth(Depth.HUD);
    tile.setInteractive({ useHandCursor: true });

    const letter = letters.get(key) ?? "?";
    const text = scene.add.text(tx, ty, letter, {
      fontFamily: "system-ui, sans-serif",
      fontSize,
      color: "#e5e7eb",
      fontStyle: "bold",
    }).setOrigin(0.5).setDepth(Depth.HUD + 1);

    tile.on("pointerdown", () => onTileClick(cell.row, cell.col));
    map.set(key, { tile, text });
  }

  return { map };
}

/** Destroy all sparse tile objects. */
export function destroyMaskTiles(objects: SparseObjects): void {
  for (const entry of objects.map.values()) {
    entry.tile.removeInteractive();
    entry.tile.removeAllListeners();
    entry.tile.destroy();
    entry.text.destroy();
  }
  objects.map.clear();
}

/** Apply the visual style for a tile state. */
export function styleTile(tile: any, state: TileState): void {
  const colors = TILE_COLORS[state];
  tile.setFillStyle(colors.fill);
  tile.setStrokeStyle(2, colors.stroke);
}

/** Enable a tile (restore alpha, re-enable interaction). */
export function enableTile(tile: any, text: any): void {
  tile.setAlpha(1);
  text.setAlpha(1);
  tile.setInteractive({ useHandCursor: true });
}

/** Disable a tile visually (dim, remove interaction). */
export function disableTile(tile: any, text: any): void {
  tile.setAlpha(0.3);
  text.setAlpha(0.15);
  tile.disableInteractive();
}
